import { createApiClient } from './client'
import type { WaypointSource } from './routes'

// Waypoint Folder Types
// WaypointSource is imported from routes.ts to avoid duplicate exports

export interface WaypointFolder {
  id: string
  user_id: string
  name: string
  color?: string
  is_system: boolean
  system_type?: 'gpx_import' | 'manual' | 'campsite_discovery' | 'poi_search' | 'trip' | null
  trip_id?: string | null
  parent_id?: string | null
  created_at: string
  updated_at: string
  // Computed fields
  depth?: number
  children?: WaypointFolder[]
  waypoint_count?: number
}

export interface WaypointFolderTree extends WaypointFolder {
  children: WaypointFolderTree[]
  waypoints?: Array<{
    id: string
    name: string
    latitude?: number
    longitude?: number
    source?: WaypointSource
    folder_id?: string | null
  }>
}

export interface CreateWaypointFolderDto {
  name: string
  parent_id?: string | null
  color?: string
}

export interface UpdateWaypointFolderDto {
  name?: string
  parent_id?: string | null
  color?: string
}

export interface MoveWaypointToFolderDto {
  waypoint_id: string
  folder_id?: string | null
}

// Waypoint Folders API class
export class WaypointFoldersApi {
  private apiClient: ReturnType<typeof createApiClient>

  constructor(getAuthToken: () => Promise<string | null>) {
    this.apiClient = createApiClient(getAuthToken)
  }

  /**
   * Get all folders for the authenticated user (tree structure)
   * GET /api/v1/itinerary/waypoint-folders
   * 
   * @deprecated Backend endpoints not yet implemented - will be reimplemented later
   */
  async getFolders(): Promise<WaypointFolderTree[]> {
    return await this.apiClient.get<WaypointFolderTree[]>('/itinerary/waypoint-folders')
  }

  /**
   * Get a single folder by ID
   * GET /api/v1/itinerary/waypoint-folders/:id
   * 
   * @deprecated Backend endpoints not yet implemented - will be reimplemented later
   */
  async getFolderById(id: string): Promise<WaypointFolder> {
    return await this.apiClient.get<WaypointFolder>(`/itinerary/waypoint-folders/${id}`)
  }

  /**
   * Create a new folder
   * POST /api/v1/itinerary/waypoint-folders
   * 
   * @deprecated Backend endpoints not yet implemented - will be reimplemented later
   */
  async createFolder(data: CreateWaypointFolderDto): Promise<WaypointFolder> {
    return await this.apiClient.post<WaypointFolder>('/itinerary/waypoint-folders', data)
  }

  /**
   * Update a folder
   * PATCH /api/v1/itinerary/waypoint-folders/:id
   * 
   * @deprecated Backend endpoints not yet implemented - will be reimplemented later
   */
  async updateFolder(id: string, updates: UpdateWaypointFolderDto): Promise<WaypointFolder> {
    return await this.apiClient.patch<WaypointFolder>(`/itinerary/waypoint-folders/${id}`, updates)
  }

  /**
   * Delete a folder
   * DELETE /api/v1/itinerary/waypoint-folders/:id
   * 
   * @deprecated Backend endpoints not yet implemented - will be reimplemented later
   */
  async deleteFolder(id: string): Promise<void> {
    return await this.apiClient.delete(`/itinerary/waypoint-folders/${id}`)
  }

  /**
   * Move a waypoint to a folder
   * POST /api/v1/itinerary/waypoint-folders/:id/move-waypoint
   * 
   * @deprecated Backend endpoints not yet implemented - will be reimplemented later
   */
  async moveWaypointToFolder(folderId: string, waypointId: string): Promise<void> {
    return await this.apiClient.post(`/itinerary/waypoint-folders/${folderId}/move-waypoint`, {
      waypoint_id: waypointId,
      folder_id: folderId,
    })
  }

  /**
   * Move waypoint to folder (alternative endpoint)
   * POST /api/v1/itinerary/waypoints/:waypointId/move-folder
   * 
   * @deprecated Backend endpoints not yet implemented - will be reimplemented later
   */
  async moveWaypoint(waypointId: string, folderId: string | null): Promise<void> {
    return await this.apiClient.post(`/itinerary/waypoints/${waypointId}/move-folder`, {
      folder_id: folderId,
    })
  }
}

